import {Model, View, ViewOptions} from 'backbone';
import * as d3 from 'd3';
import {ScaleLinear} from 'd3';
import $ from 'jquery';
import React, {ReactElement} from 'react';
import ReactDOM from 'react-dom';
import {debounce, DebouncedFunction} from 'ts-debounce';
import _ from 'underscore';

import {SequenceAlignmentRowView} from 'analyses/alignment/AlignmentRow';
import AlignmentSummaryMapView from 'analyses/alignment/AlignmentSummaryMapView';
import {AlignmentTab} from 'analyses/AlignmentTab';
import {shouldSuppressNotification} from 'BenchlingUtil';
import {BufferViewLayout} from 'buffers/BufferComps';
import PolymerBufferTabView from 'components/PolymerBufferTabView';
import * as vo from 'components/VisibilityOptions';
import {InfiniteScroller} from 'molbio/maps/linear/InfiniteScroller';
import Slice from 'molbio/maps/linear/Slice';
import Range from 'molbio/maps/selectables/Range';
import {Selection} from 'molbio/maps/selectables/Selection';
import Type from 'molbio/maps/Type';
import ProteinAlignmentRowView from 'molbio/proteins/alignments/ProteinAlignmentRowView';
import ProteinAlignmentTabState from 'molbio/proteins/alignments/ProteinAlignmentTabState';
import AlignmentTabState from 'molbio/tabstates/AlignmentTabState';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import setTestAttrs from 'test/lib/cypress/setTestAttrs';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {isAnArrowKey, isDownKey, isUpKey} from 'util/Keyboard';
import * as net from 'util/Net';
import {Optional} from 'util/Types';

import * as alignmentviewutil from './AlignmentViewUtil';

export interface ScrollAnimatorData {
  base: number;
}

interface PolymerAlignmentTabStateAttributes {
  slice: Slice;
  range: Range | null;
  loaded: boolean;
  selection: Selection;
  visibilityOptions: vo.VisibilityOptions;
  bufferViewLayout?: BufferViewLayout;
}

// Merged interface that both sequence and protein alignment tab states must implement.
export interface PolymerAlignmentTabState extends Backbone.Model {
  get<K extends keyof PolymerAlignmentTabStateAttributes>(key: K): PolymerAlignmentTabStateAttributes[K];
  set<K extends keyof PolymerAlignmentTabStateAttributes>(
    attributeName: K,
    value: PolymerAlignmentTabStateAttributes[K],
    options?: Backbone.ModelSetOptions
  ): PolymerAlignmentTabState;
  set(obj: Partial<PolymerAlignmentTabStateAttributes>): PolymerAlignmentTabState;
  getPolymerLength(): number;
  getSummaryType(): Type;
  fitSummaryTypeToWidth(chars: number, width: number, options: unknown): void;
}

export interface MinimalAlignmentRowsView extends View<Model> {
  update(): void;
  die(): void;
  resizeSVGs(): void;
  showRange(left: Optional<number>): void;
  // Only sequence-based alignments use vertical virtualization, and only on larger alignments,
  // as determined by `AlignmentAnalysis.isReallyBig()`
  infiniteScroller?: InfiniteScroller<SequenceAlignmentRowView>;
}

export default abstract class PolymerAlignmentTabView<
  M extends AlignmentTabState | ProteinAlignmentTabState,
> extends PolymerBufferTabView<M> {
  scrollAnimator!: ScrollAnimatorData;
  debouncedResizeContainer!: DebouncedFunction<() => void>;
  summaryMapView!: AlignmentSummaryMapView;
  rowsView?: MinimalAlignmentRowsView;
  topAlignmentView?: SequenceAlignmentRowView | ProteinAlignmentRowView;
  renderStatsView?(): ReactElement | null;
  getBanners?(): JSX.Element;

  static initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'alignment-view buffer-tab';
    this.prototype.attributes = {};
    this.prototype.events = {
      // @ts-expect-error
      keydown: 'handleKeyDown',
    };
  }

  initialize(options: ViewOptions<M>): void {
    super.initialize(_(options).extend({tabstate: this.model}));
    this.scrollAnimator = {base: 0};
    this.debouncedResizeContainer = debounce(() => {
      this.resizeContainer();
    }, 20);
  }

  abstract addPolymersForSearch(): void;
  abstract removePolymersForSearch(): void;
  abstract makeRowsView(): MinimalAlignmentRowsView;
  abstract makeSummaryMapView(): AlignmentSummaryMapView;
  abstract makeTopAlignmentRowView(): SequenceAlignmentRowView | ProteinAlignmentRowView;
  abstract getType(): Type;
  abstract setType(type: Type): void;
  abstract getPolymerLength(): number;
  abstract getDataTestAttrs(): DataTestAttrs;

  curScrollLeft(): number {
    return this.$el.find('.fixed-top').find('.content').scrollLeft();
  }

  getDefaultSpan(): number {
    const model: PolymerAlignmentTabState = this.model;
    const xInvFn = model.get('slice').get('xInv')!;
    return Math.floor(xInvFn(this.mapWidth()));
  }

  setMapDimensions(setStart: boolean = true): void {
    const model: PolymerAlignmentTabState = this.model;
    const xInvFn = model.get('slice').get('xInv');
    let start;
    if (xInvFn == null) {
      return;
    }
    if (setStart) {
      const left = this.curScrollLeft();
      start = Math.floor(xInvFn(left));
    } else {
      start = model.get('range')!.start();
    }

    const span = this.getDefaultSpan();
    const end = Math.min(this.getPolymerLength(), start + span);
    model.get('range')!.set({start, end, maxWidth: this.mapWidth()});
    model.get('range')!.correct();
  }

  handleArrowPress(e: JQueryEventObject): void {
    const focused = this.$el.find('.alignment-row:focus');
    if (focused.length === 0) {
      return;
    }

    if (isUpKey(e)) {
      const prev = focused.prev();
      if (prev.length > 0) {
        prev.focus();
      } else {
        focused.siblings().last().focus();
      }
    } else if (isDownKey(e)) {
      const next = focused.next();
      if (next.length > 0) {
        next.focus();
      } else {
        focused.siblings().first().focus();
      }
    }
  }

  handleKeyDown(e: JQueryEventObject): void {
    if (isAnArrowKey(e)) {
      this.handleArrowPress(e);
    }
  }

  resizeContainer(): void {
    // We want to allocate the available space between the warning banners, top alignment, the alignments, and summary view.
    // We have `totalHeight` space to allocate:
    const totalHeight = this.$el.outerHeight(true);

    // For now, we assume the summary view uses up as much space as it needs (it already optimizes for
    // minimal space usage), `bottom`, leaving us `middle` for any warning banners, the top alignment row
    // and the remaining alignments.
    const bottom = this.$('.fixed-bottom').outerHeight(true);
    const middle = totalHeight - bottom;

    // The top alignment can take up more than `middle` if it has lots of annotations. Therefore, we ensure
    // the the top alignment's height `top` leaves at least 75px for the remaining alignments... but never
    // spills out of the middle region (ie. when middle = 0). Include the possible warning section in this height
    const alignmentWarningHeight =
      this.$('.alignment-warning')
        .children() // on the first render, the outer div does not pick up the inner element's margin
        .outerHeight(true) ?? 0;
    const topAlignmentHeightAndWarning =
      this.$('.fixed-top .alignment-container').outerHeight(true) + alignmentWarningHeight;
    const remainingAlignmentsHeight = Math.min(middle, Math.max(75, middle - topAlignmentHeightAndWarning));
    const allottedTopRowAndWarning = middle - remainingAlignmentsHeight;

    // If the height we set is smaller than its content, then we will need to make the div scrollable. We
    // don't do this by default because when the container is scrollable, the dropdown and the options view
    // will not overflow out of the container. This is a rare case when dealing with a large alignment
    // on a short screen, see: T4704
    this.$('.fixed-top')
      .css('height', allottedTopRowAndWarning - alignmentWarningHeight)
      .toggleClass('fixed-top--overflow', topAlignmentHeightAndWarning <= allottedTopRowAndWarning);
    this.$('.alignments-container').css('top', allottedTopRowAndWarning);
    this.$('.alignments-container').css('bottom', bottom);
  }

  async save(): Promise<void> {
    try {
      const analysis = (this.model as AlignmentTabState).get('analysis')!;
      const response = await analysis.save(null, {
        debounce: {namespace: 'updateJSON', wait: 1000},
        parse: false,
        fileUpdate: true,
        singleton: {namespace: 'updateJSON'},
        skipFileUpdateXHRCount: true,
        skipTimeoutXHR: true,
        url: analysis.getUpdateJSONURL(),
      });
      alignmentviewutil.notifySuccess(response);
    } catch (e) {
      if (shouldSuppressNotification(e)) {
        return;
      }
      if (net.isServerRequestError(e)) {
        alignmentviewutil.notifyError(e);
        return;
      }
      throw e;
    }
  }

  killListeners(): void {
    $(this.el).off('.alignment');
    this.stopListening();
    this.listenTo(this.model, 'change:loaded rerender', function loadRerender() {
      // @ts-expect-error
      return this.update();
    });
  }

  isLoaded(): boolean {
    const model: PolymerAlignmentTabState = this.model;
    return model.get('loaded');
  }

  linkMasterSequenceToActualSequence(): void {
    // This is a hook for SequenceAlignmentTabView.
  }

  updateStatsView(): void {
    // renderStatsView will be non-null when handling protein alignments.
    if (this.renderStatsView != null) {
      // renderStatsView() will return null when the mini-map / bottom bar is disabled.
      const renderedStatsView = this.renderStatsView();
      if (renderedStatsView) {
        renderToDOM(renderedStatsView, this.$('.statsView-container')[0]);
      } else {
        ReactDOM.unmountComponentAtNode(this.$('.statsView-container')[0]);
      }
    }
  }

  updateScroller(): void {
    // When the infinite scroller is activated, the alignment view will only be re-rendered when a scroll
    // event is triggered. In cases where the display window changes size without a scroll event, we
    // manually call the scroll handler to render content that was previously out of view.
    if (this.rowsView!.infiniteScroller) {
      this.rowsView!.infiniteScroller.handleScroll();
    }
  }

  maybeRenderBanners(): void {
    if (this.getBanners) {
      renderToDOM(this.getBanners(), this.$el.find('.alignment-warning')[0], () => {
        // Banner dimensions affect rendering of the alignment, so invoke resize callback
        // _after_ the DOM is updated.
        // Defer the resize changes to ensure the React banners have fully rendered
        _.defer(() => {
          this.resizeContainer();
        });
      });
    }
  }

  registerListeners(): void {
    const model: PolymerAlignmentTabState = this.model;
    this.listenTo(model.get('bufferViewLayout'), 'change:width change:height', () => {
      this.setMapDimensions();
      this.updateScroller();
    });
    this.listenTo(model.get('range')!, 'rangeChange', (redrawOnResize: boolean, _: boolean) => {
      this.showRange(redrawOnResize);
    });
    this.listenTo(model, 'scroll', (left: number) => {
      this.showRange(false, left);
    });
    this.listenTo(model, 'rowResize', this.debouncedResizeContainer);
    if (model.get('selection')) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.listenTo(model.get('selection'), 'change:start change:end', this.updateStatsView);
    }
    this.listenTo(model.get('visibilityOptions').getOption(vo.EXPAND_MINI_MAP.name), 'change', () => {
      this.update();
      this.updateScroller();
    });

    $(window).on(`resize.alignment-${this.cid}`, this.debouncedResizeContainer);
    $(this.el).on('wheel.alignment', (e: JQueryEventObject) => {
      const rx = -(e.originalEvent as WheelEvent).deltaX / 8;
      const ry = -(e.originalEvent as WheelEvent).deltaY / 8;
      // Only handle horizontal scrolling--the AlignmentRowsView will handle vertical scrolling
      if (Math.abs(rx) > Math.abs(ry)) {
        const range = model.get('range')!;
        range.panStart(range.start() - rx);
        this.showRange(false);
        e.preventDefault();
      }
    });

    // Remove old polymers and re-add new polymers for search.
    this.removePolymersForSearch();
    this.addPolymersForSearch();
  }

  update(): void {
    const model: PolymerAlignmentTabState = this.model;
    this.killListeners();

    if (!this.isLoaded()) {
      this.$el.find('.full-container').addClass('hidden');
      this.$el.find('.loading-screen').removeClass('hidden');
      return;
    }
    this.$el.find('.loading-screen').addClass('hidden');
    this.$el.find('.full-container').removeClass('hidden');

    // TODO: This should not be happening in the view.
    this.linkMasterSequenceToActualSequence();

    this.setMapDimensions(false);

    // TODO: This should be moved to the model.
    // on first load, set the range
    // FIXME: have a designated model property for this.
    let firstLoad = false;
    if (model.get('range')!.get('maxWidth') == null) {
      // Set range default.
      const targetFont = new Type(20, 0);
      const maxWidth = this.mapWidth();
      const span = Math.min(this.getPolymerLength(), targetFont.numChars(maxWidth));
      model.get('range')!.set({start: 0, end: span, maxWidth});
      model.get('range')!.correct();
      firstLoad = true;
    }
    this.fitFontToRange();

    // Render AlignmentRowsView
    if (this.rowsView == null) {
      this.rowsView = this.makeRowsView();
      this.rowsView.setElement(this.$('.alignments-container')[0]);
      this.rowsView.$el.addClass(this.rowsView.className);
      this.rowsView.render();
    } else {
      this.rowsView.update();
    }

    this.updateStatsView();
    this.renderSummaryMapView();

    // Render top AlignmentRowView
    if (this.topAlignmentView) {
      this.topAlignmentView.die();
    }
    this.topAlignmentView = this.makeTopAlignmentRowView();
    this.$el.find('.fixed-top .alignment-container').empty().append(this.topAlignmentView.render().el);

    this.maybeRenderBanners();

    // Resize SVG's
    this.resizeSVGs();

    if (firstLoad) {
      this.showRange(true);
    } else {
      this.showRange(false);
    }

    // Register Listeners
    this.registerListeners();

    // Hack. Makes scrolling faster (by focusing for you).
    _.defer(() => $(this.topAlignmentView!.el).focus());
  }

  die(): void {
    $(this.el).off('.alignment');
    $(window).off(`resize.alignment-${this.cid}`);
    if (this.renderStatsView) {
      ReactDOM.unmountComponentAtNode(this.$('.statsView-container')[0]);
    }
    if (this.getBanners) {
      ReactDOM.unmountComponentAtNode(this.$el.find('.alignment-warning')[0]);
    }
    if (this.summaryMapView) {
      this.summaryMapView.stopListening();
    }
    if (this.rowsView) {
      this.rowsView.die();
    }
    if (this.topAlignmentView) {
      this.topAlignmentView.die();
    }
  }

  remove(): this {
    // Stop listeners on children
    this.die();
    return super.remove();
  }

  fitFontToRange(): void {
    const model: PolymerAlignmentTabState = this.model;
    const type: Type = model.get('range')!.get('type');
    this.setType(type);
    this.setSliceValues();
  }

  resizeSVGs(): void {
    this.topAlignmentView!.setSVGWidth();
    this.rowsView!.resizeSVGs();
  }

  shouldCollapseMiniMap<T extends Model>(model: T): boolean {
    if (!model) {
      return false;
    }
    return !model.get('visibilityOptions').getOption(vo.EXPAND_MINI_MAP.name).get('visible');
  }

  showRange(redraw: boolean, left?: number): void {
    const model: PolymerAlignmentTabState = this.model;
    const startBP = model.get('range')!.start();
    if (redraw) {
      this.fitFontToRange();
      this.resizeSVGs();
    }

    this.scrollAnimator.base = startBP;

    this.topAlignmentView!.showRange(left);
    this.rowsView!.showRange(left);
  }

  mapWidth(): number {
    const model: PolymerAlignmentTabState = this.model;
    // This seems incredibly delicate and maybe wrong...
    return model.get('bufferViewLayout')!.get('width') - 110; // for scroll bar
  }

  setSliceValues(): void {
    const model: PolymerAlignmentTabState = this.model;
    const start = 0;
    const end = this.getPolymerLength();
    const type = this.getType();
    const width = type.width(end - start) + type.letterSpacing;
    const x: ScaleLinear<number, number> = d3
      .scaleLinear()
      .clamp(true)
      .domain([start, end])
      .range([0, width]);
    const xInv: ScaleLinear<number, number> = d3
      .scaleLinear()
      .clamp(true)
      .domain([0, width])
      .range([start, end]);

    model.get('slice').set({
      start,
      end,
      width,
      x,
      xInv,
      maxWidth: width,
      visibleWidth: this.mapWidth(),
    });
  }

  render(): this {
    const model: PolymerAlignmentTabState = this.model;
    this.$el.empty();

    this.listenTo(this.model, 'change:loaded rerender', () => {
      this.update();
    });
    renderToDOM(<AlignmentTab />, this.$el[0]);

    if (model.get('loaded')) {
      this.update();
    } else {
      this.resizeContainer();
    }
    setTestAttrs(this.$el, this.getDataTestAttrs());
    return this;
  }

  afterAttach(): void {
    const model: PolymerAlignmentTabState = this.model;
    // Scroll alignments to correct location.
    if (model.get('loaded')) {
      this.resizeContainer();
      this.showRange(true);
    }
  }

  renderSummaryMapView(): void {
    const summaryMapViewContainer = this.$el.find('.fixed-bottom .summaryMapView-container');
    if (this.summaryMapView != null) {
      this.summaryMapView.stopListening();
      summaryMapViewContainer.empty();
    }
    this.summaryMapView = this.makeSummaryMapView();
    if (this.summaryMapView) {
      summaryMapViewContainer.append(this.summaryMapView.render().el);
    }
  }
}
PolymerAlignmentTabView.initClass();
